import { isNonStockExpenseCategory } from "@artisancode/api-types";
import { Package, Plus, Receipt } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { useProducts } from "@/hooks/use-products";
import { useRawMaterials } from "@/hooks/use-raw-materials";
import { useUnitConversions } from "@/hooks/use-unit-conversions";
import { useUoms } from "@/hooks/use-uoms";

import { ExpenseItemRow } from "./expense-item-row";

import type { ExpenseFormValues } from "./expense-schema";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

interface ExpenseItemsTableProps {
  form: UseFormReturn<ExpenseFormValues>;
  fields: FieldArrayWithId<ExpenseFormValues, "items", "id">[];
  onAddItem: (kind: "operational" | "restock") => void;
  onRemoveItem: (index: number) => void;
}

export function ExpenseItemsTable({
  form,
  fields,
  onAddItem,
  onRemoveItem,
}: ExpenseItemsTableProps) {
  const { data: rawMaterialsRes } = useRawMaterials({
    per_page: 500,
    kind: "purchased,direct",
  });
  const { data: productsRes } = useProducts({ per_page: 500 });
  const { data: uomsRes } = useUoms();
  const { data: conversionsRes } = useUnitConversions({ per_page: 500 });

  const rawMaterials = rawMaterialsRes?.items ?? [];
  const products = productsRes?.items ?? [];
  const uoms = uomsRes?.items ?? [];
  const conversions = conversionsRes?.items ?? [];

  const category = form.watch("category");
  const isNonStock = isNonStockExpenseCategory(category);
  const items = form.watch("items");
  const lines = fields.map((field, index) => ({
    field,
    index,
    restock: Boolean(items?.[index]?.itemType),
  }));
  const operational = lines.filter((l) => !l.restock);
  const restock = lines.filter((l) => l.restock);

  const renderRow = ({
    field,
    index,
  }: {
    field: (typeof fields)[number];
    index: number;
  }) => (
    <ExpenseItemRow
      key={field.id}
      form={form}
      index={index}
      canRemove={fields.length > 1}
      onRemove={() => onRemoveItem(index)}
      rawMaterials={rawMaterials}
      products={products}
      uoms={uoms}
      conversions={conversions}
    />
  );

  return (
    <div className="space-y-4">
      <FormLabel className="pt-1 text-sm font-semibold">
        Daftar Item Pengeluaran
      </FormLabel>

      <div className="space-y-4">
        {/* Operational costs */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <Receipt className="h-4 w-4 text-muted-foreground" />
              Biaya Operasional
              <span className="font-normal text-muted-foreground">
                ({operational.length})
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAddItem("operational")}
              className="h-7 gap-1.5 text-xs"
            >
              <Plus className="h-3.5 w-3.5" />
              Tambah Biaya
            </Button>
          </div>

          {operational.length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-3 text-center text-[11px] text-muted-foreground">
              Belum ada biaya operasional.
            </p>
          ) : (
            <div className="space-y-2 rounded-md border bg-muted/5 p-2">
              {operational.map(renderRow)}
            </div>
          )}
        </section>

        {/* Restocked goods */}
        {!isNonStock && (
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  <Package className="h-4 w-4" />
                  Barang Restock (Masuk Stok)
                  <span className="font-normal text-muted-foreground">
                    ({restock.length})
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  Bahan baku atau produk yang dibeli; stok bertambah saat
                  disimpan.
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onAddItem("restock")}
                className="h-7 gap-1.5 border-emerald-300 text-xs text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
              >
                <Plus className="h-3.5 w-3.5" />
                Tambah Barang Restock
              </Button>
            </div>

            {restock.length === 0 ? (
              <p className="rounded-md border border-dashed border-emerald-200 px-3 py-3 text-center text-[11px] text-muted-foreground dark:border-emerald-900">
                Belum ada barang restock. Klik &quot;Tambah Barang Restock&quot;
                untuk mencatat bahan baku yang dibeli.
              </p>
            ) : (
              <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/20 p-2 dark:border-emerald-900 dark:bg-emerald-950/10">
                {restock.map(renderRow)}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
